import { usePathname, useSearchParams } from 'next/navigation'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useStatsigExperiment } from '@src/experiments/statsig-integration/useStatsigExperiment'
import { useQuinceNprogressRouter as useRouter } from '@src/modules/navigation' // This is intentional for making the progress bar work for subcollection
import { FilterSection, LoaderWrapper } from '@src/quince-components/src'
import { BreadcrumbSchema } from '@src/quince-components/src/atoms/breadCrumb'
import { getDirectory } from '@src/quince-components/src/atoms/breadCrumb/breadcrumb.helper'
import { If } from '@src/quince-components/src/atoms/conditionals'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import { PRODUCT_SORT_VALUES } from '@src/quince-site-common/src/constants/product'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useCaptureDataException from '@src/quince-site-common/src/hooks/useCaptureDataException'
import useNewCollectionSortV2 from '@src/quince-site-common/src/hooks/useCLPSortExperimentV2'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import useMounted from '@src/quince-site-common/src/hooks/useMounted'
import { useAllQueryState } from '@src/quince-site-common/src/hooks/useQueryState'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong/somethingWentWrong'
import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'
import filterFalsy from '@src/quince-site-common/src/utils/filterFalsy'
import { getCurrentPageGenderContext } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { cloneDeep, pickBy } from '@src/utils/functions/utils'

import {
  trackingMetaData,
  COLOR_PAGES_RENDERED_PATHS,
  COLLECTION_TYPES_WITH_CLP_PERSONALIZED,
} from './collection.constants'
import {
  isFeedDynamic,
  getQueryParamsForSelectedFilters,
  modifyFilteredCollectionOnProductFeed,
  getTrackerUrlForSelectedFilters,
  getProductTypeWithPageForStaticCollections,
  splitCollectionSubSections,
  getNavigateTo,
  getVariantsListFromCollectionNew,
} from './collection.helpers'
import styles from './collectionPage.module.scss'
import Collection, { SubCollection, COLLECTION_PAGE_TYPE } from './domains/Collection'
import { Filter, SelectedFilter, UpdateQueryParams } from './domains/CollectionFilter'
import { CustomSectionLazyLoader } from './molecules/custom-section-lazy-loader/custom-section-lazy-loader.component'
import { CustomCollectionPageSection } from './molecules/customCollectionPageSection/CustomCollectionPageSection'
import NoProduct from './molecules/noProducts'
import { SubCategoryList } from './molecules/subCategoryList'
import { SubCollectionCardList } from './molecules/subCollectionCardList'
import SubCollectionSection from './molecules/subCollectionSection'
import collectionService from './services/collectionService/CollectionService'
import { ECollectionType } from './services/collectionService/CollectionService.types'
import {
  shouldShowSubCollectionCards,
  shouldShowBorder,
  getFormattedSlug,
  getShowSecondImageOnProductTilesValue,
  getCollectionCacheKey,
  isUndefined,
} from './utils'

const QUERY_KEYS: ('filter' | 'sortBy')[] = ['filter', 'sortBy']
export function CollectionPage({
  collectionPage,
  pathname: collectionPathname,
  searchParams: collectionSearchParams,
}: Readonly<{
  collectionPage: Collection
  pathname: string
  searchParams: string
}>) {
  const cacheKey = getCollectionCacheKey(collectionPathname, collectionSearchParams)
  // Please make sure to put the state which is needed in the component to make collection page cache work
  // We are not using useAppSelector here because we are not using react state management for the cache
  // We are storing only JSON of the collection page to feed the props/state/refs of the component
  const { page: pageCache, hasPaginationTerminated: hasPaginationTerminatedCache } =
    collectionCacheStateManager.getCollectionCache(cacheKey) ?? {}

  const isStaticPage = collectionPage.collectionType === (ECollectionType.STATIC as string)

  const isMounted = useMounted()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const hasPaginationTerminated = useRef<boolean>(hasPaginationTerminatedCache ?? false)
  const [watchedQueryStates, setWatchedQueryStates] = useAllQueryState<'filter' | 'sortBy'>(QUERY_KEYS)
  const { selectedFilters, sortBy } = useMemo(() => {
    const selectedFilters = watchedQueryStates.filter
      ? SelectedFilter.getSelectedFilterFromQueryParams(watchedQueryStates.filter)
      : new SelectedFilter({})
    const sortBy = watchedQueryStates.sortBy ?? SORT_BY.FEATURED.value

    return { selectedFilters, sortBy }
  }, [watchedQueryStates])

  const productViewTracker = `${collectionPage.slug}_${TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW}`
  useBatchedAnalyticsForProductCard({
    productViewTracker,
    trackingMetaData,
  })

  // CLP Sort Experiment
  const { experimentBucket: clpPersonalizedBucket } = useStatsigExperiment(STATSIG_EXPERIMENTS.CLP_PERSONALIZED)

  const isClpPersonalizedPage = useMemo(() => {
    return COLLECTION_TYPES_WITH_CLP_PERSONALIZED.includes(collectionPage.collectionType)
  }, [collectionPage.collectionType])

  const isClpPersonalized = useMemo(() => {
    return clpPersonalizedBucket === 'show' && isClpPersonalizedPage
  }, [clpPersonalizedBucket, isClpPersonalizedPage])

  const { init: statsigInitialized, baseSortBy, isCLPV2ExperimentBucket } = useNewCollectionSortV2(isStaticPage)

  const showSecondImageOnProductTiles = getShowSecondImageOnProductTilesValue(collectionPage.slug)

  const initialSubCollections = collectionPage.subCollections

  const [filteredSubCollections, setFilteredSubCollections] =
    useState<(SubCollection | CustomSection)[]>(initialSubCollections)

  const [initialFilters, setInitialFilters] = useState<Filter[]>(collectionPage.filters)
  const [nextFeedURL, setNextFeedURL] = useState<string>(collectionPage.nextFeedURL ?? '')

  const [showSubCollectionsPageLoader, setShowSubCollectionsPageLoader] = useState<boolean>(
    !isUndefined(pageCache) ? false : isFeedDynamic(collectionPage.slug),
  )

  const isJumpLink = searchParams.get('jumplink') === 'true'

  const [showShimmerOnProductCards, setShowShimmerOnProductCards] = useState(
    !isUndefined(pageCache) ? false : isStaticPage || isClpPersonalizedPage,
  )

  const [showSomethingWentWrong, setShowSomethingWentWrong] = useState(false)
  const { zipCode } = useZipCode()

  const showSubCollectionCards = shouldShowSubCollectionCards(collectionPage.subCollectionCards)

  const filteredList = filteredSubCollections.filter(
    sc => sc.typename === 'CustomSection' || (sc.productCards?.length ?? -1) > 0,
  )

  const isFilteredSubCollectionsEmpty = !filteredSubCollections.length
  useCaptureDataException({
    captureException: isFilteredSubCollectionsEmpty,
    errorMsg: `No section is present for collection page: ${collectionPage.slug}`,
    name: 'Collection page section check',
    module: MODULES.COLLECTION_FILTER,
    feature: FEATURES.FILTER_CHANGE,
  })

  const totalProductsCurrentlyOnPage = filteredList
    .filter(subcollection => subcollection.typename === 'SubCollection')
    .map(subcollection => subcollection.productCards)
    .flat().length

  const [totalProductsCount, setTotalProductsCount] = useState<number>(collectionPage.totalCount ?? 0)

  const isProductAbsent = totalProductsCurrentlyOnPage === 0

  useCaptureDataException({
    captureException: isProductAbsent,
    errorMsg: `No products on collection page: ${collectionPage.slug}`,
    name: 'Collection page number of products check',
    module: MODULES.COLLECTION_FILTER,
    feature: FEATURES.FILTER_CHANGE,
  })

  const filterSectionRef = useRef<HTMLDivElement>(null)
  const productSectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (showSubCollectionsPageLoader) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [showSubCollectionsPageLoader])

  useEffect(() => {
    if (!isMounted || getFormattedSlug(collectionPage.slug) !== getFormattedSlug(pathname)) {
      trackEvent(TELEMETRY_EVENT_NAMES.COLLECTION_VIEW, {
        collectionSlug: getFormattedSlug(collectionPage.slug),
        collectionTitle: collectionPage.title,
        variantList: getVariantsListFromCollectionNew(filteredList),
        filterList: initialFilters,
        zipCode: zipCode,
        isDynamicCollectionPage: isFeedDynamic(collectionPage.slug),
      })
      setFindingMethod[FINDING_METHOD_AGGREGATE.LANDING_PAGE]?.()
    }
  }, [pathname, searchParams])

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  // For infinite Feed.
  const [intersectRef, isIntersecting] = useIntersect(null, isDesktop ? '3500px' : '2000px', 0)
  const [showNextFeedLoading, setShowNextFeedLoading] = useState(false)
  const [page, setPage] = useState(pageCache ?? 0)

  useEffect(() => {
    if (isIntersecting && !showSomethingWentWrong && !hasPaginationTerminated.current && !showShimmerOnProductCards) {
      if (totalProductsCount <= totalProductsCurrentlyOnPage) return

      if (totalProductsCurrentlyOnPage === 0 && page != 0) return

      if (
        !nextFeedURL &&
        (['trendingThisWeek', 'pickedForYou', 'lastChance'].includes(collectionPage.collectionType) ||
          isClpPersonalized)
      )
        return

      let newPage = page + 1
      let productType
      if (['static', 'allNewArrivals'].includes(collectionPage.collectionType)) {
        const {
          productType: newProductType,
          page: newPageStatic = 0,
          doNotFetch,
        } = getProductTypeWithPageForStaticCollections(
          filteredSubCollections.filter(sc => sc.typename === 'SubCollection') as SubCollection[],
          newPage,
        )

        if (doNotFetch) return

        newPage = newPageStatic
        productType = newProductType
      } else {
        const doNotFetch = (
          filteredSubCollections.filter(sc => sc.typename === 'SubCollection') as SubCollection[]
        ).every(sc => {
          if (sc.productCards === undefined) return true

          if (sc.productCards.length === 0) return false

          if (sc.totalProducts === sc.productCards.length) {
            return true
          }
          return false
        })

        if (doNotFetch) return
      }

      setShowNextFeedLoading(true)
      setShowSomethingWentWrong(false)

      const queryParams = getQueryParamsForSelectedFilters(selectedFilters, sortBy, collectionPage.slug)
      collectionService
        .getNextPageCollection({
          slug: collectionPage.slug,
          nextFeedURL,
          filter: queryParams.filter,
          sortBy: (queryParams.sortBy as PRODUCT_SORT_VALUES | undefined) ?? baseSortBy,
          baseSortBy,
          collectionType: collectionPage.collectionType,
          currentSlug: pathname,
          page: newPage,
          productType,
          isClpPersonalized,
          collectionId: collectionPage.contentful_id,
        })
        .then(resp => {
          setPage(newPage)
          if (
            ['newArrivals', 'root', 'bestseller'].includes(collectionPage.collectionType) &&
            !resp.subCollections.length
          ) {
            hasPaginationTerminated.current = true
          }
          const newFilteredSubCollections = cloneDeep(filteredSubCollections)

          modifyFilteredCollectionOnProductFeed(
            resp,
            newFilteredSubCollections,
            newPage,
            collectionPage.slug,
            false,
            collectionPage,
            isClpPersonalized,
            isCLPV2ExperimentBucket,
          )

          setShowNextFeedLoading(false)
          setNextFeedURL(resp.next_url)
          setFilteredSubCollections(newFilteredSubCollections)
        })
        .catch((e: unknown) => {
          setShowNextFeedLoading(false)

          setShowSomethingWentWrong(true)
          captureException(e, {
            module: MODULES.COLLECTION_PRODUCT_LOAD,
            feature: FEATURES.PAGINATION,
          })
        })
    }
  }, [
    isIntersecting,
    collectionPage.slug,
    page,
    filteredSubCollections,
    showSomethingWentWrong,
    totalProductsCurrentlyOnPage,
    collectionPage.collectionType,
    totalProductsCount,
    pathname,
    nextFeedURL,
    selectedFilters,
    sortBy,
    baseSortBy,
    collectionPage,
    showShimmerOnProductCards,
    isClpPersonalized,
  ])

  useEffect(() => {
    if (
      (showSubCollectionsPageLoader && isFeedDynamic(collectionPage.slug)) ||
      (showShimmerOnProductCards && statsigInitialized)
    ) {
      // Initial API Call
      const newPage = 0
      const queryParams = getQueryParamsForSelectedFilters(selectedFilters, sortBy, collectionPage.slug)
      collectionService
        .getNextPageCollection({
          slug: collectionPage.slug,
          nextFeedURL,
          page: newPage,
          filter: queryParams.filter,
          sortBy: (queryParams.sortBy as PRODUCT_SORT_VALUES | undefined) ?? baseSortBy,
          baseSortBy,
          collectionType: collectionPage.collectionType,
          currentSlug: pathname,
          isClpPersonalized,
          collectionId: collectionPage.contentful_id,
        })
        .then(resp => {
          const newFilteredSubCollections = cloneDeep(filteredSubCollections)

          modifyFilteredCollectionOnProductFeed(
            resp,
            newFilteredSubCollections,
            newPage,
            collectionPage.slug,
            true,
            collectionPage,
            isClpPersonalized,
          )
          setFilteredSubCollections(newFilteredSubCollections)

          if (resp.next_url) {
            setNextFeedURL(resp.next_url)
          }

          if (resp.total) {
            setTotalProductsCount(resp.total)
          }

          if (isFeedDynamic(collectionPage.slug) || isClpPersonalized) setInitialFilters(resp.filters)
          setShowSubCollectionsPageLoader(false)

          setShowShimmerOnProductCards(false)
        })
        .catch((e: unknown) => {
          setShowShimmerOnProductCards(false)
          setShowSomethingWentWrong(true)
          setShowSubCollectionsPageLoader(false)
          captureException(e, {
            module: MODULES.COLLECTION_PRODUCT_LOAD,
            feature: FEATURES.PAGINATION,
          })
        })
    }
  }, [
    collectionPage.slug,
    filteredSubCollections,
    page,
    selectedFilters,
    showSubCollectionsPageLoader,
    sortBy,
    collectionPage.collectionType,
    pathname,
    nextFeedURL,
    baseSortBy,
    statsigInitialized,
    showShimmerOnProductCards,
    collectionPage,
    isClpPersonalized,
  ])

  useEffect(() => {
    if (getFormattedSlug(collectionPage.slug) !== getFormattedSlug(pathname))
      setShowSubCollectionsPageLoader(!isUndefined(pageCache) ? false : isFeedDynamic(collectionPage.slug))
  }, [pathname, collectionPage.slug, pageCache])

  useEffect(() => {
    setShowSubCollectionsPageLoader(!isUndefined(pageCache) ? false : isFeedDynamic(collectionPage.slug))
  }, [searchParams, collectionPage.slug, pageCache])

  useEffect(() => {
    setInitialFilters(collectionPage.filters)
  }, [collectionPage.filters])

  useEffect(() => {
    setFilteredSubCollections(collectionPage.subCollections)
  }, [collectionPage.subCollections])

  useEffect(() => {
    setNextFeedURL(collectionPage.nextFeedURL ?? '')
  }, [collectionPage.nextFeedURL])

  useEffect(() => {
    setTotalProductsCount(collectionPage.totalCount ?? 0)
  }, [collectionPage.totalCount, collectionPage.collectionType])

  useEffect(() => {
    if (isUndefined(pageCache) && isJumpLink && filterSectionRef.current && productSectionRef.current) {
      const filterSectionHeight = filterSectionRef.current.clientHeight
      const productSectionTop = productSectionRef.current.getBoundingClientRect().top
      const topOfElement = productSectionTop - filterSectionHeight
      window.scroll({ top: topOfElement, behavior: 'smooth' })
    }
  }, [isJumpLink, pageCache])

  useEffect(() => {
    // Update the cache with the latest data after the component has done rendering
    queueMicrotask(() => {
      collectionCacheStateManager.setCollectionCache({
        collectionPage: {
          ...collectionPage,
          subCollections: filteredSubCollections,
          nextFeedURL,
          filters: initialFilters,
          totalCount: totalProductsCount,
        },
        page,
        hasPaginationTerminated: hasPaginationTerminated.current,
      })
    })
  }, [
    filteredSubCollections,
    nextFeedURL,
    initialFilters,
    totalProductsCount,
    page,
    collectionPathname,
    collectionSearchParams,
    collectionPage,
  ])

  useEffect(() => {
    return () => {
      const cacheKey = getCollectionCacheKey(collectionPathname, collectionSearchParams)
      collectionCacheStateManager.publishCollectionCache(cacheKey)
    }
  }, [collectionPathname, collectionSearchParams])

  //Prevent clicking on already selected subcollection to maintain consistent behavior with production.
  const naviagteToSelectedSubcategoryPage = (_slug: string | undefined) => {
    if (_slug) {
      const slug = getFormattedSlug(_slug)
      const currentPath = window.location.pathname // Get the current pathname
      const newPath = `/${slug}?jumplink=true`

      if (currentPath !== `/${slug}`) {
        // Only redirect if the slugs are different
        router.push(newPath)
      }
    }
  }

  const subCollectionsForCardList = collectionPage.subCollectionCards

  function updateQuery({ filter, sort }: UpdateQueryParams) {
    let updatedSelectedFilters = selectedFilters
    let updatedSortBy = sortBy
    if (filter) {
      switch (filter.type) {
        case 'remove': {
          updatedSelectedFilters = SelectedFilter.removeValueFromSelectedFilter(
            selectedFilters,
            filter.value,
            filter.name,
          )
          break
        }
        case 'toggle': {
          updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
            selectedFilters,
            filter.name,
            filter.value,
          )
          trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
            label: filter.label,
            value: filter.value,
            context: {
              collectionSlug: collectionPage.slug,
              collectionTitle: collectionPage.title,
              isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
              selectedFilters: pickBy(updatedSelectedFilters, v => v !== undefined),
            },
          })
          // ISSUE: @tech-debt productType shouldn't be a filter and isn't a filter - forced custom logic shouldn't be added
          if (filter.name === 'productType') router.push(`/${filter.value}?jumplink=true`)
          break
        }
        case 'reset': {
          updatedSelectedFilters = new SelectedFilter({})
          break
        }
        default:
          break
      }
    }
    if (sort) {
      switch (sort.type) {
        case 'toggle': {
          const sortValue = sort.value
          updatedSortBy = sortValue === sortBy ? SORT_BY.FEATURED.value : sortValue
          break
        }
        case 'reset': {
          updatedSortBy = SORT_BY.FEATURED.value
          break
        }
        default:
          break
      }
      const isDefaultOption = updatedSortBy === SORT_BY.FEATURED.value
      if (!isDefaultOption) {
        trackEvent(TELEMETRY_EVENT_NAMES.SORT_USED, {
          sort_used: updatedSortBy,
        })
      }
    }
    if (updatedSortBy === sortBy && SelectedFilter.checkEquality(updatedSelectedFilters, selectedFilters)) return

    const updatedWatchedQueryState = {
      filter: SelectedFilter.getUpdatedSelectedFilterStringForWindowURL(updatedSelectedFilters),
      sortBy: updatedSortBy === SORT_BY.FEATURED.value ? undefined : updatedSortBy,
    }
    const navigateToOverwrite = getNavigateTo(updatedSelectedFilters, pathname)
    const targetURL = setWatchedQueryStates(updatedWatchedQueryState, navigateToOverwrite)
    const isSamePath = new URL(targetURL, window.location.href).toString() === window.location.href
    setShowSubCollectionsPageLoader(!isSamePath)
  }

  const directory = useMemo(() => getDirectory(pathname), [pathname])
  const collectionGender = getCurrentPageGenderContext(collectionPage.slug, '')

  const hideCollectionPageTitle = collectionPage.slug === '/collections/trending'

  const showMobileCollectionBanner = !isDesktop && collectionPage.collectionBannerMobile
  const showDesktopCollectionBanner = isDesktop && collectionPage.collectionBannerDesktop

  const { subCollections, customSections } = splitCollectionSubSections(filteredList)
  return (
    <div>
      {showMobileCollectionBanner ? (
        <CustomCollectionPageSection
          filteredSubCollection={collectionPage.collectionBannerMobile}
          filteredSubCollections={[]}
          slug={collectionPage.slug}
          isMounted={isMounted}
        />
      ) : (
        <>
          {!hideCollectionPageTitle && (
            <h1
              className={`mobileOnly d-flex justify-content--center font-family--grosa font-weight--500 text-transform--uppercase ${styles.collection_header}`}
            >
              {isJumpLink ? collectionPage.subCollections[0]?.title : collectionPage.title}
            </h1>
          )}
          <SubCategoryList
            linkGroup={{
              buttons: filterFalsy(
                isDesktop ? collectionPage.subcategoriesLinkDesktop : collectionPage.subcategoriesLinkMobile,
              ),
            }}
            directory={directory}
          />
        </>
      )}

      <div ref={filterSectionRef} className={showMobileCollectionBanner ? styles.filterBorderTop : undefined}>
        <FilterSection
          noOfItems={totalProductsCount}
          filters={initialFilters}
          sortBy={sortBy}
          selectedFilters={selectedFilters}
          updateQuery={updateQuery}
          showSelectedOption={(sortBy as PRODUCT_SORT_VALUES) !== PRODUCT_SORT_VALUES.FEATURED}
          shouldRenderColorFilterHtml={COLOR_PAGES_RENDERED_PATHS.has(collectionPage.slug)}
          collectionSlug={collectionPage.slug}
          hideFilters={collectionPage.collectionPageType === COLLECTION_PAGE_TYPE.BUNDLE}
          showShimmer={showShimmerOnProductCards}
        />
      </div>
      {showSubCollectionCards && (
        <SubCollectionCardList
          subCollections={subCollectionsForCardList}
          onCardClick={subCollection => {
            trackEvent(TELEMETRY_EVENT_NAMES.SUBCOLLECTION_FILTER_USED, {
              filter_available: subCollectionsForCardList.map(item => item.title),
              filter_used: subCollection.title,
            })
            naviagteToSelectedSubcategoryPage(subCollection.slug)
          }}
          slug={collectionPage.slug}
        />
      )}
      <LoaderWrapper showLoader={showSubCollectionsPageLoader && !showSomethingWentWrong}>
        <div ref={productSectionRef}>
          <>
            {showDesktopCollectionBanner ? (
              <CustomCollectionPageSection
                filteredSubCollection={collectionPage.collectionBannerDesktop}
                filteredSubCollections={[]}
                slug={collectionPage.slug}
                isMounted={isMounted}
              />
            ) : (
              <>
                {!hideCollectionPageTitle && !!collectionPage.title && (
                  <h1
                    data-testid={TEST_LOCATORS.COLLECTION_TITLE}
                    className={`desktopOnly d-flex justify-content--center font-family--grosa font-weight--500 text-transform--uppercase ${styles.collection_header}`}
                  >
                    {collectionPage.title}
                  </h1>
                )}
              </>
            )}
            {totalProductsCount > 0 ? (
              <div className={styles.subcollection_wrapper}>
                {subCollections.map((filteredSubCollection, index, array) => {
                  return (
                    <React.Fragment key={filteredSubCollection.id}>
                      <If condition={filteredSubCollection.typename !== 'CustomSection'}>
                        <SubCollectionSection
                          key={filteredSubCollection.title}
                          subCollectionData={filteredSubCollection}
                          showBorderBottom={shouldShowBorder(index, array)}
                          isFirstCollection={index === 0}
                          tracker={`collection_page__${
                            collectionPage.slug
                          }${getTrackerUrlForSelectedFilters(selectedFilters)}`}
                          additionalAttributes={{
                            ...collectionPage.additionalAttributes,
                            ...filteredSubCollection.additionalAttributes,
                          }}
                          doHolidayCutoffSorting={!collectionPage.nextFeedURL}
                          collectionGender={collectionGender}
                          collectionSlug={collectionPage.slug}
                          productViewTracker={productViewTracker}
                          showSecondImageOnProductTiles={showSecondImageOnProductTiles}
                          showShimmerOnProductCards={showShimmerOnProductCards}
                        />
                      </If>
                    </React.Fragment>
                  )
                })}
                <div id="boundary_bottom" className={styles.boundary_bottom} ref={intersectRef} />
                <LoaderWrapper
                  testid={TEST_LOCATORS.COLLECTION_INFINITE_LOADER}
                  showLoader={showNextFeedLoading && !showSomethingWentWrong}
                  minHeight={'40rem'}
                >
                  <div />
                </LoaderWrapper>
                {customSections.map(filteredCustomSection => {
                  return (
                    <CustomSectionLazyLoader
                      key={`${collectionPage.slug}_${filteredCustomSection.id}`}
                      slug={collectionPage.slug}
                      isPageCache={!isUndefined(pageCache)}
                    >
                      <CustomCollectionPageSection
                        filteredSubCollection={filteredCustomSection}
                        filteredSubCollections={filteredSubCollections}
                        slug={collectionPage.slug}
                        isMounted={isMounted}
                      />
                    </CustomSectionLazyLoader>
                  )
                })}
              </div>
            ) : (
              <NoProduct
                onResetFilters={() => {
                  updateQuery({ filter: { type: 'reset' }, sort: { type: 'reset' } })
                }}
              />
            )}
          </>
        </div>
      </LoaderWrapper>
      <SomethingWentWrong show={showSomethingWentWrong} minHeight={'20rem'} />
      <BreadcrumbSchema directory={directory} />
    </div>
  )
}
